/**
 * Authentication Service
 * Handles user login, logout, and session management with HTTP-only cookies
 */

import { apiClient } from '@/lib/api/client';
import type { Credentials, AuthResponse, User } from '@/types/auth.types';
import { mockLogin, mockLogout, mockGetCurrentUser, mockIsAuthenticated } from './mockAuthService';

// Check if mock mode is enabled
const USE_MOCK_DATA = import.meta.env['VITE_USE_MOCK_DATA'] === 'true';

/**
 * Login with email and password
 * Sets HTTP-only cookie in the backend (or localStorage in mock mode)
 */
export async function login(credentials: Credentials): Promise<User> {
  if (USE_MOCK_DATA) {
    const result = await mockLogin(credentials.email, credentials.password);
    return result.user;
  }

  const response = (await apiClient.post('/auth/login', credentials)) as AuthResponse;
  return response.data;
}

/**
 * Logout current user
 * Clears HTTP-only cookie in the backend (or localStorage in mock mode)
 */
export async function logout(): Promise<void> {
  if (USE_MOCK_DATA) {
    await mockLogout();
    return;
  }

  await apiClient.post('/auth/logout');
}

/**
 * Get current user information
 * Validates the HTTP-only cookie (or localStorage in mock mode)
 */
export async function getCurrentUser(): Promise<User> {
  if (USE_MOCK_DATA) {
    const user = await mockGetCurrentUser();
    if (!user) {
      throw new Error('No authenticated user');
    }
    return user;
  }

  const response = (await apiClient.get('/auth/me')) as AuthResponse;
  return response.data;
}

/**
 * Check if user is authenticated
 * Returns true if valid session exists
 */
export async function checkAuth(): Promise<boolean> {
  if (USE_MOCK_DATA) {
    return mockIsAuthenticated();
  }

  try {
    await getCurrentUser();
    return true;
  } catch {
    return false;
  }
}
